import { resolveName } from 'components/form'
import { getIn } from 'final-form'
import { RefObject, useCallback, useState } from 'react'
import { useForm } from 'react-final-form'
import { isUndefinedOrNull } from 'util/checks'
import { MetaPath } from 'util/metaPath'
import { focusElementAfterDelay, scrollAndFocusElement, ScrollOptions, scrollToBottom } from 'util/scroll'

import { ConsultaSeguimentoFieldModel } from '../model-consultaSeguimento'

const SCROLL_CONFIG: ScrollOptions = {
  delay: 300,
  motionDurations: [
    { maxDistance: 400, duration: 200 },
    { maxDistance: 800, duration: 500 },
  ],
  motionMaxDuration: 700,
}

type ScrollCondition<T, K> = {
  if: (value: K, allValues: T) => boolean
  scrollTo: keyof T | 'BOTTOM' | typeof FOCUS_GO_NEXT_BUTTON
}

type DependentFieldConfig<T> = {
  fieldName: keyof T
  scrollConditions: ScrollCondition<T, T[keyof T]>[]
}

type ScrolledFieldState = {
  fieldName: string
  lastScrollConditionIndex: number | null
}

type ScrollableFieldConfig<T, K> = {
  scrollConditions: ScrollCondition<T, K>[]
  dependsOn?: keyof T
  isList?: boolean
}

export type ScrollableFieldsConfig<T> = {
  [Key in keyof Partial<T>]: ScrollableFieldConfig<T, T[Key]>
}

export const SCROLL_TO_RESULTADO_ID = 'resultado'

/**
 * Usar quando o elemento do resultado já estiver acima do campo que disparou o scroll
 * (caso raro em que o resultado aparece no meio do fluxo, não ao final). Nesse caso não
 * faz sentido rolar a tela de volta para cima; apenas move o foco para o botão "Continuar".
 */
export const FOCUS_GO_NEXT_BUTTON = 'FOCUS_GO_NEXT_BUTTON'

export function useConsultaSeguimentoAutoScroll<T>({
  name,
  containerRef,
  goNextButtonRef,
  scrollConfig,
}: {
  name: MetaPath<T>
  containerRef: RefObject<HTMLElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
  scrollConfig: ScrollableFieldsConfig<T>
}) {
  const { getState: getFormState } = useForm<ConsultaSeguimentoFieldModel>()

  const [scrolledFields, setScrolledFields] = useState<ScrolledFieldState[]>(() =>
    initScrolledFields(scrollConfig, getFormState().values, name)
  )

  const getId = useCallback((elementId: keyof T): string => {
    return elementId.toString()
  }, [])

  const getPerguntaValues = useCallback((): T => {
    return getIn(getFormState().values, resolveName(name))
  }, [getFormState, name])

  const getSubPerguntaValue = useCallback(
    (fieldName: keyof T): T[keyof T] => {
      return getIn(getFormState().values, resolveName((name as { [K in keyof T]: MetaPath<T[K]> })[fieldName]))
    },
    [getFormState, name]
  )

  const getFieldScrollState = useCallback(
    (fieldName: keyof T): ScrolledFieldState | undefined => {
      return scrolledFields.find((scrolledField) => scrolledField.fieldName === fieldName)
    },
    [scrolledFields]
  )

  const resolveDependentField = useCallback(
    (fieldName: keyof T): DependentFieldConfig<T> | null => {
      const keys = Object.keys(scrollConfig) as Array<keyof T>
      const dependentName = keys.find((key) => scrollConfig[key]?.dependsOn === fieldName)

      if (!dependentName) return null

      return { fieldName: dependentName, scrollConditions: scrollConfig[dependentName]?.scrollConditions ?? [] }
    },
    [scrollConfig]
  )

  const updateScrollState = useCallback((fieldName: keyof T, lastScrollConditionIndex: number) => {
    setScrolledFields((prev) =>
      prev.map(
        (scrolledField): ScrolledFieldState => {
          if (fieldName === scrolledField.fieldName) {
            return {
              ...scrolledField,
              lastScrollConditionIndex,
            }
          }
          return scrolledField
        }
      )
    )
  }, [])

  const shouldRedirectScrollToDependent = useCallback(
    (fieldName: keyof T, dependent: DependentFieldConfig<T>, isRedirectedCall: boolean): boolean => {
      const dependentScrollState = getFieldScrollState(dependent.fieldName)
      const hasDependentBeenScrolled = !isUndefinedOrNull(dependentScrollState?.lastScrollConditionIndex)
      const isList = scrollConfig[fieldName]?.isList ?? false

      return hasDependentBeenScrolled && (!isList || isRedirectedCall)
    },
    [scrollConfig, getFieldScrollState]
  )

  const performScroll = useCallback(
    (scrollCondition: ScrollCondition<T, T[keyof T]>): void => {
      if (scrollCondition.scrollTo === 'BOTTOM') {
        scrollToBottom(containerRef, SCROLL_CONFIG)
        return
      }

      if (scrollCondition.scrollTo === FOCUS_GO_NEXT_BUTTON) {
        focusElementAfterDelay(goNextButtonRef.current, SCROLL_CONFIG)
        return
      }

      const elementId = scrollCondition.scrollTo.toString()
      const isResultado = elementId === SCROLL_TO_RESULTADO_ID

      scrollAndFocusElement(
        elementId,
        SCROLL_CONFIG,
        goNextButtonRef.current,
        isResultado
          ? undefined
          : (focusTarget) => {
              focusTarget.addEventListener('focusout', (e) => e.stopPropagation(), { once: true })
            },
        'start'
      )
    },
    [containerRef, goNextButtonRef]
  )

  const executeScroll = useCallback(
    (
      scrollCondition: ScrollCondition<T, T[keyof T]>,
      fieldName: keyof T,
      conditionIndex: number,
      isRedirectedCall: boolean = false
    ): boolean => {
      const conditionMatches = scrollCondition.if(getSubPerguntaValue(fieldName), getPerguntaValues())
      if (!conditionMatches) return false

      const dependent = resolveDependentField(fieldName)
      if (dependent && shouldRedirectScrollToDependent(fieldName, dependent, isRedirectedCall)) {
        return dependent.scrollConditions.some((dependentCondition, dependentIndex) =>
          executeScroll(dependentCondition, dependent.fieldName, dependentIndex, true)
        )
      }

      const fieldScrollState = getFieldScrollState(fieldName)
      const isDeduplicated = fieldScrollState?.lastScrollConditionIndex === conditionIndex
      if (isDeduplicated) return false

      performScroll(scrollCondition)
      updateScrollState(fieldName, conditionIndex)
      return true
    },
    [
      getSubPerguntaValue,
      getPerguntaValues,
      resolveDependentField,
      shouldRedirectScrollToDependent,
      getFieldScrollState,
      performScroll,
      updateScrollState,
    ]
  )

  const triggerScrollFor = useCallback(
    (fieldName: keyof T): void => {
      const scrollConditions = scrollConfig[fieldName]?.scrollConditions

      if (!scrollConditions) return

      for (let conditionIndex = 0; conditionIndex < scrollConditions.length; conditionIndex++) {
        if (executeScroll(scrollConditions[conditionIndex], fieldName, conditionIndex)) {
          break
        }
      }
    },
    [executeScroll, scrollConfig]
  )

  return { getId, triggerScrollFor }
}

function initScrolledFields<T>(
  scrollConfig: ScrollableFieldsConfig<T>,
  formState: object,
  name: MetaPath<T>
): ScrolledFieldState[] {
  const formValues = getIn(formState, resolveName(name)) as T
  return Object.keys(scrollConfig).map((fieldName) => {
    const key = fieldName as keyof T
    const conditions = scrollConfig[key]?.scrollConditions ?? []
    const subValue = getIn(formState, resolveName((name as { [K in keyof T]: MetaPath<T[K]> })[key]))
    const matchingIndex = conditions.findIndex((condition) => condition.if(subValue, formValues))
    return {
      fieldName,
      lastScrollConditionIndex: matchingIndex >= 0 ? matchingIndex : null,
    }
  })
}
